import React, { useState } from 'react';
import { get as _get } from 'lodash';
import { OceanProfile, OceanTrait, DiscProfile, DiscTrait } from '../../types';
import Icon from '../common/Icon';

interface OceanProfileCardProps {
  ocean: OceanProfile;
  oceanTraits: OceanTrait[];
  disc?: DiscProfile | null;
  discTraits?: DiscTrait[] | null;
  animated: boolean;
  onSaveToLibrary: () => void;
}

type ProfileMode = 'ocean' | 'disc';

const OceanProfileCard: React.FC<OceanProfileCardProps> = ({
  ocean,
  oceanTraits,
  disc,
  discTraits,
  animated,
  onSaveToLibrary,
}) => {
  const [mode, setMode] = useState<ProfileMode>('ocean');

  const oceanList = oceanTraits && oceanTraits.length > 0 ? oceanTraits : [];
  const discList = discTraits && discTraits.length > 0 ? discTraits : [];
  const hasDisc = disc != null && discList.length > 0;

  // Fall back to ocean-only view if no DISC data
  if (oceanList.length === 0) {
    return null;
  }

  const activeTraits = mode === 'ocean' ? oceanList : discList;
  const activeProfile = mode === 'ocean' ? ocean : disc;

  return (
    <div className="bg-white border-2 border-brand-black rounded-brand p-8 shadow-sm flex flex-col">
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <Icon name="bar_chart" className="text-brand-pink" />
          {mode === 'ocean' ? 'OCEAN Profile' : 'DISC Profile'}
        </h3>

        {hasDisc && (
          <div className="flex items-center bg-brand-grey01 rounded-pill p-1 gap-1 text-[10px] font-extrabold uppercase tracking-widest">
            <button
              onClick={() => setMode('ocean')}
              className={`px-3 py-1.5 rounded-pill transition-all duration-200 ${
                mode === 'ocean'
                  ? 'bg-brand-black text-white shadow-sm'
                  : 'text-brand-grey08 hover:text-brand-black'
              }`}
            >
              OCEAN
            </button>
            <button
              onClick={() => setMode('disc')}
              className={`px-3 py-1.5 rounded-pill transition-all duration-200 ${
                mode === 'disc'
                  ? 'bg-brand-black text-white shadow-sm'
                  : 'text-brand-grey08 hover:text-brand-black'
              }`}
            >
              DISC
            </button>
          </div>
        )}
      </div>

      <div className="space-y-10 flex-1">
        {activeTraits.map(({ label, key, color, desc }) => {
          const value = (_get(activeProfile, key) as number | null) ?? 0;

          return (
            <div key={key} className="space-y-3">
              <div className="flex justify-between items-end text-[10px] font-extrabold uppercase tracking-widest">
                <span className="text-brand-black">{label}</span>
                <span className="font-black text-lg">{value}%</span>
              </div>

              <div className="bg-brand-grey01 h-3.5 rounded-full w-full relative">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ease-out ${color} shadow-sm`}
                  style={{ width: animated ? `${value}%` : '0%' }}
                />
              </div>

              {desc && (
                <p className="text-[11px] leading-relaxed text-brand-grey08 font-medium">
                  {desc}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OceanProfileCard;
